import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { getJSON } from '@/lib/api'
import { useDebouncedValue } from '@/pages/RenewalPipelineListControls'
import type { Association } from '@/pages/StaffSignInPage'

interface ListingEvent {
  id: number
  name: string
  schedule: string
  location: string | null
  hero_image: { filename: string; content_type: string; url: string } | null
  lowest_price: string
  lowest_price_cents: number
}

interface EventsListingResponse {
  events: ListingEvent[]
  months: { value: string; label: string }[]
}

export function PublicEventsListingPage({ association }: { association: Association }) {
  const [search, setSearch] = React.useState('')
  const [month, setMonth] = React.useState('')
  const debouncedSearch = useDebouncedValue(search, 250)
  const query = useQuery({
    queryKey: ['public-events', debouncedSearch, month],
    queryFn: () => getJSON<EventsListingResponse>(listingPath(debouncedSearch, month)),
    retry: false,
  })

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto flex h-14 max-w-6xl items-center px-5 text-body-sm font-medium">
          {association.name}
        </div>
      </header>
      <div className="mx-auto max-w-3xl space-y-6 px-5 py-8">
        <header className="space-y-1">
          <h1 className="text-heading font-normal tracking-tight text-foreground">Events</h1>
          <p className="text-caption text-muted-foreground">{association.name} · upcoming events</p>
        </header>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full max-w-xs">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
              aria-hidden="true"
            />
            <Input
              className="h-8 pl-8 text-caption"
              aria-label="Search events"
              placeholder="Search events"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5" aria-label="Filter events by month">
            <MonthButton label="All" selected={month === ''} onClick={() => setMonth('')} />
            {query.data?.months.map((item) => (
              <MonthButton
                key={item.value}
                label={item.label}
                selected={month === item.value}
                onClick={() => setMonth(item.value)}
              />
            ))}
          </div>
        </div>

        {query.isLoading ? (
          <p className="text-body-sm text-muted-foreground" role="status">
            Loading events…
          </p>
        ) : query.isError || !query.data ? (
          <Alert>Events unavailable.</Alert>
        ) : query.data.events.length === 0 ? (
          <div className="rounded border border-dashed border-border px-6 py-12 text-center">
            <p className="text-caption text-muted-foreground">No upcoming events.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {query.data.events.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
    </main>
  )
}

function MonthButton({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      aria-pressed={selected}
      className={
        selected
          ? 'h-8 rounded-full border-foreground/20 bg-accent px-3 text-caption font-medium text-foreground'
          : 'h-8 rounded-full px-3 text-caption font-normal'
      }
      onClick={onClick}
    >
      {label}
    </Button>
  )
}

function EventCard({ event }: { event: ListingEvent }) {
  const [heroImageAvailable, setHeroImageAvailable] = React.useState(Boolean(event.hero_image))

  return (
    <Card className="overflow-hidden transition-colors hover:border-primary/40">
      <a href={`/events/${event.id}`} className="flex gap-4 p-3">
        {event.hero_image && heroImageAvailable ? (
          <img
            src={event.hero_image.url}
            alt=""
            className="h-16 w-24 shrink-0 rounded object-cover"
            onError={() => setHeroImageAvailable(false)}
          />
        ) : (
          <div
            className="h-16 w-24 shrink-0 rounded bg-gradient-to-br from-primary/20 via-accent to-muted"
            aria-hidden="true"
          />
        )}
        <div className="min-w-0 flex-1 self-center space-y-0.5 leading-snug">
          <p className="text-caption font-medium text-primary">{event.schedule}</p>
          <h2 className="text-body-sm font-medium text-foreground">{event.name}</h2>
          {event.location && (
            <p className="truncate text-caption text-muted-foreground">{event.location}</p>
          )}
        </div>
        <p className="shrink-0 self-center text-body-sm font-medium tabular-nums text-foreground">
          {event.lowest_price_cents === 0 ? 'Free' : `From ${event.lowest_price}`}
        </p>
      </a>
    </Card>
  )
}

function listingPath(search: string, month: string) {
  const params = new URLSearchParams()
  if (search.trim()) params.set('search', search.trim())
  if (month) params.set('month', month)
  const query = params.toString()
  return `/api/v1/public/events${query ? `?${query}` : ''}`
}
